import { useTranslation } from 'react-i18next'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'

import { categoricalColors } from '@/features/dashboard/chart-colors'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/ui/chart'
import { withGaps, type Gap, type Sample } from '../health-history'

const TIME = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' })
const MOMENT = new Intl.DateTimeFormat(undefined, { timeStyle: 'medium' })
// The palette is built to the length asked for, so both are there.
const [FIRST, SECOND] = categoricalColors(2) as [string, string]

/**
 * The browser's record of the host, drawn.
 *
 * Two charts rather than one, because a percentage and a task count do not share
 * an axis. Nothing animates: the charts redraw every poll, and a line that sweeps
 * in each time never holds still long enough to read.
 */
export function HealthCharts({
  samples,
  interval,
}: {
  samples: readonly Sample[]
  interval: number
}) {
  const { t } = useTranslation()
  const drawn = withGaps(samples, interval)

  return (
    <section className="space-y-3 rounded-lg border p-4 bg-background on-surface">
      <div>
        <h2 className="font-medium">{t('admin.history')}</h2>
        <p className="text-muted-foreground text-sm">{t('admin.historyWhy')}</p>
      </div>

      {samples.length < 2 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          {t('admin.historyCollecting')}
        </p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <HistoryChart
            title={t('admin.chartResources')}
            points={drawn}
            domain={[0, 100]}
            config={{
              cpu: { label: t('admin.seriesCpu'), color: FIRST },
              memory: { label: t('admin.seriesMemory'), color: SECOND },
            }}
          />
          <HistoryChart
            title={t('admin.chartWork')}
            points={drawn}
            config={{
              busy: { label: t('admin.seriesBusy'), color: FIRST },
              queued: { label: t('admin.seriesQueued'), color: SECOND },
              capacity: {
                label: t('admin.seriesSlots'),
                color: 'var(--muted-foreground)',
              },
            }}
          />
        </div>
      )}
    </section>
  )
}

/**
 * One chart over the record, a line per entry in `config`.
 *
 * `capacity` is a limit rather than a measure, so it is drawn as a dashed step
 * in muted ink: it changes only when a worker starts or stops.
 */
function HistoryChart({
  title,
  points,
  config,
  domain,
}: {
  title: string
  points: (Sample | Gap)[]
  config: ChartConfig
  domain?: [number, number]
}) {
  return (
    <figure className="space-y-2">
      <figcaption className="text-sm font-medium">{title}</figcaption>
      <ChartContainer config={config} className="aspect-auto h-48 w-full">
        <LineChart data={points}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="at"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(at: number) => TIME.format(at)}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={32}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={32}
            allowDecimals={false}
            {...(domain === undefined ? {} : { domain })}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(_, payload) => {
                  const at = (payload[0]?.payload as Sample | undefined)?.at
                  return at === undefined ? '' : MOMENT.format(at)
                }}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          {Object.keys(config).map((key) => (
            <Line
              key={key}
              dataKey={key}
              stroke={`var(--color-${key})`}
              strokeWidth={2}
              {...(key === 'capacity'
                ? { type: 'stepAfter' as const, strokeDasharray: '4 4' }
                : { type: 'monotone' as const })}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ChartContainer>
    </figure>
  )
}
